/**
 * AuthedImage — renders an image from a backend endpoint that requires the
 * bearer token. A plain <img src> can't send the Authorization header, so we
 * fetch the bytes through the authenticated `api` instance, turn them into an
 * object URL and render that. Reports load failures via onError so callers can
 * show an "unavailable" state.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Loader2 } from 'lucide-react';

interface Props {
  path: string;                 // API path relative to /api, e.g. /imports/photos/PHO-1/thumb
  alt?: string;
  className?: string;
  onError?: () => void;
  testid?: string;
}

// Module-level cache of fetched blobs keyed by path, so re-opening the same
// image (e.g. flipping through the insights modal) is instant and never
// re-hits the network.
const _blobCache = new Map<string, Blob>();

export const AuthedImage: React.FC<Props> = ({ path, alt, className, onError, testid }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setUrl(null);
    (async () => {
      try {
        let blob = _blobCache.get(path);
        if (!blob) {
          const r = await api.get<Blob>(path, { responseType: 'blob' });
          blob = r.data;
          _blobCache.set(path, blob);
        }
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!revoked) onError?.();
      } finally {
        if (!revoked) setLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  if (loading) {
    return <Loader2 className="animate-spin text-raptor-navy/40" size={20} data-testid={testid ? `${testid}-loading` : undefined} />;
  }
  if (!url) return null;
  return <img src={url} alt={alt} className={className} data-testid={testid} />;
};

export default AuthedImage;
